// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TitleTypography from "@keller/App/Components/TitleTypography/TitleTypography";
import XngCarousel, { SwiperStyle } from "@keller/App/Components/XngCarousel/XngCarousel";
import { linkArrowStyle, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

interface IndustryCard {
    fields: {
        image: string;
        title: string;
        description: string;
        link: LinkFieldValue;
        openInNewTab: boolean;
        bannerTypeDropDown: string;
        status: string;
    };
}

const enum fields {
    title = "title",
    industries = "industries",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.industries]: IndustryCard[];
    };
}

type Props = OwnProps;

export interface IndustryCardStyles {
    outerWrapper?: InjectableCss;
    industryItem?: {
        link?: LinkPresentationProps;
        wrapper?: InjectableCss;
        image?: LazyImageProps;
    };
    swiperStyles?: SwiperStyle;
}

export const industryCardStyles: IndustryCardStyles = {
    outerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
        `,
    },
    industryItem: {
        link: {
            ...mergeWithCss(linkArrowStyle, {
                css: css`
                    margin-top: 18px;
                `,
            }),
        },
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: 240px;
                ${xScreen("lte", "xs")} {
                    width: 216px;
                }
            `,
        },
        image: {
            css: css`
                min-height: 130px;
                border-radius: 8px;
                width: 240px;
                height: 130px;
                ${xScreen("lte", "xs")} {
                    width: 216px;
                }

                img {
                    height: 100%;
                }
            `,
        },
    },
    swiperStyles: {
        container: {
            css: css`
                .swiper-slide {
                    width: 240px;
                }

                ${xScreen("gt", "md")} {
                    .xng-pagination-container {
                        display: none;
                    }
                }
                ${xScreen("lte", "xs")} {
                    .swiper-slide {
                        width: 216px;
                    }
                }
            `,
        },
    },
};

const styles = industryCardStyles;

const IndustryCardWidget: React.FC<Props> = ({ fields: { industries, title } }) => {
    const { links, hasMissingCategories } = useGetLinks(industries, o => o.fields.link);

    if (!industries || industries.length === 0 || hasMissingCategories) {
        return null;
    }

    return (
        <StyledWrapper {...styles.outerWrapper} data-test-selector="industryCardWidget">
            <TitleTypography>{translate(title)}</TitleTypography>
            <XngCarousel
                navigation={false}
                slidesPerView="auto"
                extendedStyles={styles.swiperStyles}
                pagination={false}
            >
                {industries?.map((industry, index) => {
                    const industryUrl = links[index].url;
                    return (
                        <StyledWrapper
                            key={industry.fields.title}
                            {...styles.industryItem?.wrapper}
                            data-test-selector={`${industry.fields.title}_industry_item`}
                        >
                            <Clickable href={industryUrl}>
                                <LazyImage
                                    src={industry.fields.image}
                                    altText={title}
                                    {...styles.industryItem?.image}
                                />
                            </Clickable>
                            <Link href={industryUrl} {...styles.industryItem?.link} area-label={title}>
                                {industry.fields.title}
                            </Link>
                        </StyledWrapper>
                    );
                })}
            </XngCarousel>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: IndustryCardWidget,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Industries We Serve",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.industries,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "image",
                        displayName: "Industry image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { value: "", type: "Url" },
                        isRequired: true,
                        validate: (link: LinkFieldValue) => {
                            if (!link || !link.value) {
                                return "A value must be provided.";
                            }
                            return null;
                        },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
